"use client";

import React, { useEffect, useRef, useState } from "react";
import * as twgl from "twgl.js";

// --- shaders ---
const fragmentShader = `#version 300 es
precision highp float;
out vec4 fragColor;

uniform vec2 u_resolution;
uniform float u_time;
uniform sampler2D u_video;
uniform float u_ratio;
uniform float u_charSize;
uniform bool u_isMobile;

#define R u_resolution

float character(int n, vec2 p) {
  p = floor(p*vec2(-4., 4.) + 2.5);
  if (clamp(p.x, 0., 4.) == p.x) {
      if (clamp(p.y, 0., 4.) == p.y) {
          int a = int(round(p.x) + 5. * round(p.y));
          if (((n >> a) & 1) == 1) return 1.;
      }	
  }
  return 0.;
}

void main(){ 
  vec2 F = gl_FragCoord.xy;
  
  float size = u_charSize; 
  float srez = size * 2.0;

  vec2 pixelated_st = floor(F.xy / srez) * srez / R;

  vec2 uv = pixelated_st;
  float screenAspect = R.x / R.y;

  if (screenAspect > u_ratio) {
    float scale = u_ratio / screenAspect; 
    uv.y = (uv.y - 0.5) * scale + 0.5;
  } else {
    float scale = screenAspect / u_ratio; 
    uv.x = (uv.x - 0.5) * scale + 0.5;
  }

  vec3 col = texture(u_video, uv).rgb;
  
  if (u_isMobile) {
    col = col * 1.3; 
    col = (col - 0.5) * 1.2 + 0.5; 
    col = clamp(col, 0.0, 1.0);
  }
  
  float gray = .3 * col.r + .6 * col.g + .1 * col.b;

  int n = 0;

  if (gray > 0.0233) n = 4096;     
  if (gray > 0.0465) n = 131200;   
  if (gray > 0.0698) n = 4329476;  
  if (gray > 0.0930) n = 459200;   
  if (gray > 0.1163) n = 4591748;  
  if (gray > 0.1395) n = 12652620; 
  if (gray > 0.1628) n = 14749828; 
  if (gray > 0.1860) n = 18393220; 
  if (gray > 0.2093) n = 15239300; 
  if (gray > 0.2326) n = 17318431; 
  if (gray > 0.2558) n = 32641156; 
  if (gray > 0.2791) n = 18393412; 
  if (gray > 0.3023) n = 18157905; 
  if (gray > 0.3256) n = 17463428; 
  if (gray > 0.3488) n = 14954572; 
  if (gray > 0.3721) n = 13177118; 
  if (gray > 0.3953) n = 18405034; 
  if (gray > 0.4186) n = 16269839; 
  if (gray > 0.4419) n = 15018318; 
  if (gray > 0.4651) n = 18400814; 
  if (gray > 0.4884) n = 33081316; 
  if (gray > 0.5116) n = 15255086; 
  if (gray > 0.5349) n = 32045584; 
  if (gray > 0.5581) n = 6566222;  
  if (gray > 0.5814) n = 15022158; 
  if (gray > 0.6047) n = 18444881; 
  if (gray > 0.6279) n = 16272942; 
  if (gray > 0.6512) n = 18415153; 
  if (gray > 0.6744) n = 32641183; 
  if (gray > 0.6977) n = 32540207; 
  if (gray > 0.7209) n = 18732593; 
  if (gray > 0.7442) n = 18667121; 
  if (gray > 0.7674) n = 16267326; 
  if (gray > 0.7907) n = 32575775; 
  if (gray > 0.8140) n = 15022414; 
  if (gray > 0.8372) n = 15255537; 
  if (gray > 0.8605) n = 32032318; 
  if (gray > 0.8837) n = 32045617; 
  if (gray > 0.9070) n = 33061392; 
  if (gray > 0.9302) n = 33061407; 
  if (gray > 0.9535) n = 32045630; 
  if (gray > 0.9767) n = 11512810; 

  vec2 p = mod(F.xy/size, 2.) - vec2(1);
  col = col*character(n, p);
  fragColor = vec4(col, 1);
}
`;

const vertexShader = `#version 300 es
precision highp float;
in vec4 position;
void main() {
  gl_Position = vec4( position );
}
`;

const containerStyle: React.CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: 0,
  overflow: "hidden",
  backgroundColor: "black",
  margin: 0,
  padding: 0,
};

const canvasStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  height: "100%",
};

const AsciiVideo = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 600;
      const newSrc = isMobile ? "/einsteinmobile.mp4" : "/einstein-3.mp4";
      setVideoSrc(prev => prev === newSrc ? prev : newSrc);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const gl = canvas.getContext("webgl2", {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
    });
    if (!gl) return console.error("WebGL2 not supported");

    video.crossOrigin = "anonymous";
    video.loop = true;
    video.muted = true;
    video.playsInline = true;

    const programInfo = twgl.createProgramInfo(gl, [vertexShader, fragmentShader]);
    if (!programInfo?.program) {
      console.error("AsciiVideo shader program failed to link");
      return;
    }

    const arrays = {
      position: [-1, -1, 0, 1, -1, 0, -1, 1, 0, -1, 1, 0, 1, -1, 0, 1, 1, 0],
      texcoord: { numComponents: 2, data: [0,0,1,0,0,1,0,1,1,0,1,1] }
    };
    const bufferInfo = twgl.createBufferInfoFromArrays(gl, arrays);

    // Create a 1×1 black placeholder - video data is uploaded per-frame in the render loop
    // (avoids texImage2D: no video and glGenerateMipmap errors from twgl trying to
    //  upload the video element before video.src is set)
    const videoTex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, videoTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const textures = { video: videoTex };

    let frameId: number;

    const startRendering = () => {
      const render = (time: number) => {
        if (!gl || !video) return;

        // resize for DPR
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        twgl.resizeCanvasToDisplaySize(gl.canvas as HTMLCanvasElement, dpr);

        const width = window.innerWidth;
        const isMobile = width < 600;

        const targetColumns = isMobile ? 80 : (width < 1024 ? 120 : 200);
        let calculatedSize = Math.max(2.5, gl.canvas.width / targetColumns / 2);
        if (isMobile) calculatedSize = Math.max(3.5, calculatedSize);
        calculatedSize = Math.floor(calculatedSize);

        // ✅ Only update texture if video has data
        if (video.readyState >= video.HAVE_CURRENT_DATA && video.videoWidth > 0 && video.videoHeight > 0) {
          gl.bindTexture(gl.TEXTURE_2D, textures.video);
          gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);

          // The frame is uploaded with texImage2D rather than filled into storage
          // sized from videoWidth/videoHeight: those report the display size, which
          // Safari lets differ from the decoded frame (rotation metadata, non-square
          // pixels), and the fill then overruns its allocation with a
          // "texSubImage2D: Offset overflows texture dimensions" error. An upload
          // sized from the frame itself cannot disagree with it.
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
        }

        gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
        gl.useProgram(programInfo.program);
        twgl.setBuffersAndAttributes(gl, programInfo, bufferInfo);

        const ratio = video.videoWidth > 0 ? video.videoWidth / video.videoHeight : 16/9;

        twgl.setUniforms(programInfo, {
          u_time: time * 0.001,
          u_video: textures.video,
          u_resolution: [gl.canvas.width, gl.canvas.height],
          u_ratio: ratio,
          u_charSize: calculatedSize,
          u_isMobile: isMobile,
        });

        twgl.drawBufferInfo(gl, bufferInfo);
        frameId = requestAnimationFrame(render);
      };

      frameId = requestAnimationFrame(render);
    };

    video.addEventListener("canplay", startRendering, { once: true });
    return () => {
      cancelAnimationFrame(frameId);
      video.removeEventListener("canplay", startRendering);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoSrc) return;
    video.pause();
    video.src = videoSrc;
    video.load();
    video.play().catch(err => { if (err.name !== 'AbortError') console.error(err); });
  }, [videoSrc]);

  return (
    <div style={containerStyle}>
      <canvas ref={canvasRef} style={canvasStyle} />
      <video ref={videoRef} style={{ display: "none" }} playsInline muted loop />
    </div>
  );
};

export default AsciiVideo;
